import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { querySuiteRuns } from '@/features/executions/data';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildSuiteRunPath } from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import {
  queryAllTableV2NestedRows,
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2NameCell,
  TableV2NestedCollapse,
  TableV2PassRateCell,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
  tableV2NestedBodyRowHeightPx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import { TableV2SkeletonRow } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import {
  countSuiteRunTests,
  defaultNestedSuiteRunSort,
} from '@/features/suites/components/SuitesTableV2.helpers';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@/ui/primitives';
import { SuiteRunExpandedTests } from './SuiteRunExpandedTests';
import { buildSuiteRunHeader, type SortableNestedSuiteRunField } from './SuitesTableV2Headers';
import { suiteRunColgroup, suiteRunSkeletonColumns } from './SuitesTableV2Layout';
import type { TagColor } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteRunDto, TestRunDto } from '@flowqa/shared';

const SuiteRunNestedRow = ({
  actionsDisabled,
  deleteRowPendingId,
  isLastVisibleRow,
  linksDisabled,
  row,
  sortingDisabled,
  tagColorByName,
  testRunsQueryFn,
  testRunDeleteRowPendingId,
  testRunUpdateRowTagsColorPendingId,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onDeleteTestRunRow,
  onUpdateRowTagsColor,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  linksDisabled: boolean;
  row: SuiteRunDto;
  sortingDisabled: boolean;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  testRunDeleteRowPendingId: string | null;
  testRunUpdateRowTagsColorPendingId: string | null;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: SuiteRunDto) => void;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: SuiteRunDto; tags: string[]; colors: TagColor[] }) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const name = row.displayName ?? row.suiteName ?? row.name ?? row.id;
  const suiteRunNumberLabel = formatTableV2RunNumber(row.runNumber) ?? '-';
  const suiteRunHref = buildSuiteRunPath(row.suiteSlug ?? row.suiteId, row.runNumber);
  const testCount = countSuiteRunTests(row).total;

  return (
    <>
      <TableRow
        sx={[
          tableV2NestedDepthSx[1].bodyRow,
          {
            '& > td': {
              borderBottom:
                expanded || isLastVisibleRow
                  ? 0
                  : (theme) => `1px solid ${theme.app.colors.border}`,
              height: `${String(tableV2NestedBodyRowHeightPx)}px`,
              py: s('sm'),
              verticalAlign: 'middle',
            },
          },
        ]}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse test runs' : 'Expand test runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={linksDisabled ? null : suiteRunHref}
            primary={suiteRunNumberLabel}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <TableV2PassRateCell counts={countSuiteRunTests(row)} />
        </TableCell>
        <TableCell>
          <TableV2DurationCell value={row.durationMs} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.timestamp} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          {actionsDisabled ? null : (
            <TableActionsCell
              rowName={name}
              verticalAlign="center"
              deleteActionLabel={getEntityActionLabel('delete', 'Suite Run')}
              editTagsEntityNoun="suite run"
              rowTags={row.tags ?? []}
              onUpdateTagsColor={(input) => {
                onUpdateRowTagsColor({ row, ...input });
              }}
              updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
              onDelete={() => {
                onDeleteRow(row);
              }}
              disableDelete={deleteRowPendingId === row.id}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={8}>
          <SuiteRunExpandedTests
            actionsDisabled={actionsDisabled}
            childCount={testCount}
            deleteRowPendingId={testRunDeleteRowPendingId}
            expanded={expanded}
            linksDisabled={linksDisabled}
            organisationId={row.organisationId}
            projectId={row.projectId}
            sortingDisabled={sortingDisabled}
            tagColorByName={tagColorByName}
            suiteRunId={row.id}
            testRunsQueryFn={testRunsQueryFn}
            updateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const SuiteExpandedRuns = ({
  actionsDisabled,
  childCount,
  deleteRowPendingId,
  expanded,
  linksDisabled,
  organisationId,
  projectId,
  suiteId,
  suiteRunsQueryFn,
  sortingDisabled,
  tagColorByName,
  testRunsQueryFn,
  testRunDeleteRowPendingId,
  testRunUpdateRowTagsColorPendingId,
  updateRowTagsColorPendingId,
  onDeleteSuiteRunRow,
  onDeleteTestRunRow,
  onUpdateSuiteRunRowTagsColor,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  childCount: number;
  deleteRowPendingId: string | null;
  expanded: boolean;
  linksDisabled: boolean;
  organisationId: string | null;
  projectId: string;
  suiteId: string;
  suiteRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteRunDto>>;
  sortingDisabled: boolean;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  testRunDeleteRowPendingId: string | null;
  testRunUpdateRowTagsColorPendingId: string | null;
  updateRowTagsColorPendingId: string | null;
  onDeleteSuiteRunRow: (row: SuiteRunDto) => void;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onUpdateSuiteRunRowTagsColor: (input: {
    row: SuiteRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultNestedSuiteRunSort);
  const handleSortChange = (field: SortableNestedSuiteRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const header = buildSuiteRunHeader({
    sort,
    sortingDisabled,
    onSortChange: handleSortChange,
  });
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.suiteRuns,
      'v2-nested',
      organisationId,
      projectId,
      suiteId,
      sort.field,
      sort.direction,
    ),
    enabled: expanded,
    queryFn: async () => {
      const input = {
        page: 1,
        pageSize: 1,
        sorts: [sort],
        filters: buildEqFilter('suiteId', suiteId),
      };
      return await queryAllTableV2NestedRows(
        input,
        suiteRunsQueryFn ??
          (async (nextInput) => await querySuiteRuns(projectId, organisationId, nextInput)),
      );
    },
    staleTime: 30_000,
  });
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <Box sx={tableV2NestedDepthSx[1].frame}>
            <Table aria-label="Suite runs loading" sx={tableV2NestedDepthSx[1].table}>
              {suiteRunColgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`suite-runs-nested-skeleton-${String(index)}`}
                    columns={suiteRunSkeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : query.data && query.data.items.length > 0 ? (
          <Box sx={tableV2NestedDepthSx[1].frame}>
            <Table aria-label="Suite runs" sx={tableV2NestedDepthSx[1].table}>
              {suiteRunColgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((suiteRun, index, suiteRuns) => (
                  <SuiteRunNestedRow
                    key={suiteRun.id}
                    actionsDisabled={actionsDisabled}
                    deleteRowPendingId={deleteRowPendingId}
                    isLastVisibleRow={index === suiteRuns.length - 1}
                    linksDisabled={linksDisabled}
                    row={suiteRun}
                    sortingDisabled={sortingDisabled}
                    tagColorByName={tagColorByName}
                    testRunsQueryFn={testRunsQueryFn}
                    testRunDeleteRowPendingId={testRunDeleteRowPendingId}
                    testRunUpdateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
                    updateRowTagsColorPendingId={updateRowTagsColorPendingId}
                    onDeleteRow={onDeleteSuiteRunRow}
                    onDeleteTestRunRow={onDeleteTestRunRow}
                    onUpdateRowTagsColor={onUpdateSuiteRunRowTagsColor}
                    onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Text role="body" color="textSecondary">
            No data
          </Text>
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};
